import React, { useState, useMemo } from 'react';
import { Table } from 'antd';
import { CheckSquareFilled, CloseCircleFilled } from 'antd/icons';
import useTodoActions from '@hooks/useTodoActions';
import AvailabilityModal from '@components/Modal/AvailabilityModal';
import useAuth from '@vl-core/useAuth';
import { ToDoItem } from '@components/Patients/ToDos/ToDos';
import { Case } from 'vl-common/src/schemas/shared';
import { useTranslation } from 'react-i18next';
import differenceInHours from 'date-fns/differenceInHours';
import { css } from '@emotion/react';
import useTheme from '@hooks/useTheme';

const actionButtonStyle = css`
  width: 100%;
  height: 100%;
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  font: inherit;
  cursor: pointer;
  appearance: none;
  text-align: left;
  display: block;
  whitespace: normal;

  &:focus-visible {
    outline: 2px solid inherit;
  }
`;

enum StatusText {
  Complete = 'Complete',
  CompleteEditable = 'Complete (Editable)',
  ReminderSent = 'Patient reminder sent',
  ToBeCompleted = 'Order to be completed',
  NewOrder = 'New order',
  Rejected = 'Rejected'
}

interface TodoTableProps {
  todoList: ToDoItem[];
  caseData: Partial<Case>;
  allTodosComplete: boolean;
  refetch: () => void;
}
const ToDoTable = ({ todoList, caseData, allTodosComplete, refetch }: TodoTableProps) => {
  const { user } = useAuth();
  const { t } = useTranslation('common');
  const { isHBSTheme } = useTheme();

  const [case_id, setCase_id] = useState<number | null>(null);
  const [request, setRequest] = useState(false);
  const [apptType, setApptType] = useState('');

  const textColour = (isHighlighted: boolean) => {
    if (isHighlighted) return 'red';
    if (isHBSTheme) return 'var(--productPrimaryDark)';
    else return 'var(--productPrimary)';
  };

  const showAvailabilityModal = (caseId: number | null, apptType: string) => {
    setCase_id(caseId);
    setApptType(apptType);
    setRequest(true);
  };

  const validTodos = useMemo(() => {
    return (
      todoList?.filter((todo) => {
        if (todo.todo_type === 'explan') {
          const matchesCase = caseData?.case_id ? todo.case_id === caseData.case_id : true;
          const hasExplanFile = Boolean(todo.file_guid);
          return matchesCase && hasExplanFile;
        }
        return true;
      }) || []
    );
  }, [todoList, caseData?.case_id]);

  const todoActions = useTodoActions(validTodos, showAvailabilityModal, refetch);

  const columns = [
    {
      title: null,
      dataIndex: 'status',
      key: 'checkbox',
      render: (text: StatusText) => {
        if (text === StatusText.Complete || text === StatusText.CompleteEditable) {
          return (
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <CheckSquareFilled css={{ color: 'green', scale: '130%' }} />
            </div>
          );
        }
        if (text === StatusText.Rejected) {
          return (
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <CloseCircleFilled css={{ color: 'red', scale: '130%' }} />
            </div>
          );
        }
        return null;
      },
      width: '10%'
    },
    {
      title: 'Your actions',
      dataIndex: 'action_desc',
      key: 'status',
      render: (text: string, todoItem: ToDoItem, index: number) => {
        const hoursOverdue = differenceInHours(new Date(), todoItem.datetime);
        const isHighlighted = todoItem.status === StatusText.ToBeCompleted && hoursOverdue >= 72;
        if (todoActions[index].action) {
          return (
            <button
              onClick={todoActions[index].action || undefined}
              css={actionButtonStyle}
              aria-label={`${todoActions[index].text} - view action`}
              style={{ color: textColour(isHighlighted) }}
              data-testid={todoItem.test_id}
            >
              {todoActions[index].text}
            </button>
          );
        }
        return (
          <div css={{ whiteSpace: 'normal', textAlign: 'left', color: isHighlighted ? 'red' : 'initial' }}>
            {todoActions[index].text}
          </div>
        );
      },
      ellipsis: true,
      width: '70%'
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: '110px',
      render: (text: StatusText, todoItem: ToDoItem) => {
        const hoursOverdue = differenceInHours(new Date(), todoItem.datetime);
        const isHighlighted = text === StatusText.ToBeCompleted && hoursOverdue >= 72;

        return (
          <p
            style={{
              color: isHighlighted ? 'red' : 'initial'
            }}
          >
            {text}
          </p>
        );
      }
    }
  ];
  return (
    <div>
      <h2
        style={{
          fontSize: '20px',
          fontWeight: '600',
          paddingTop: '30px',
          paddingBottom: '15px'
        }}
      >
        {caseData?.cases_dropdown}
      </h2>
      {!!caseData?.case_closed_date && (
        <div css={{ fontSize: '13px', marginBottom: '20px' }}>
          {t('This case has been ')}
          <b>{t('discharged')}</b>
          {t('. Please complete any remaining actions below, as no further actions will be pro')}
        </div>
      )}
      {allTodosComplete && (
        <>
          <h3
            css={{
              fontSize: '15px',
              fontWeight: '600'
            }}
          >
            {t('No actions required')}
          </h3>
          <div css={{ fontSize: '13px' }}>
            {t('No actions required at the moment. We are currently processing your condition an')}
          </div>
        </>
      )}
      {todoList && (
        <>
          <Table
            columns={columns}
            dataSource={validTodos}
            pagination={false}
            style={{ paddingBottom: '30px', paddingTop: '20px' }}
          />
          {t("Go to Documents to see questions you've answered")}
        </>
      )}
      <AvailabilityModal
        open={request}
        setRequest={setRequest}
        user={user}
        case_id={case_id}
        apptType={apptType}
        updateActiveList={refetch}
      />
    </div>
  );
};

export default ToDoTable;
